import React, { useState } from "react";
import toast from 'react-hot-toast';
import {
  getExpedienteDeMascota,
  actualizarVacuna,
  eliminarVacuna,
  eliminarVacunasImportadas,
  crearEntradaExpediente,
  crearVacuna
} from "../../services/api";
import ProximasSection from "./ProximasSection";

export default function CartillaSalud({
  cartillaSectionOpen,
  setCartillaSectionOpen,
  cartillaData,
  vacunas,
  setVacunas,
  desparasitaciones,
  setDesparasitaciones,
  entradas,
  setEntradas,
  objetoId,
  mascotaInfo,
  isVetAccess,
  canUseIA,
  isStandardPlan,
  loadingVacunas,
  loadingDesparasitaciones,
  tratamientosActivos,
  proximasConRecordatorio,
  setProximasConRecordatorio,
  creandoRecordatorio,
  setCreandoRecordatorio,
  onOpenVisitaModal,
  onAddQuickEntry,
  onCompartir,
  onExportCartilla,
  loadingShare,
  loadingCartillaExport,
  onShowImportCartilla,
  onShowQuickReminder,
  onVerDashboardTratamiento
}) {
  const [vacunasSectionOpen, setVacunasSectionOpen] = useState(cartillaSectionOpen);
  const [desparasitacionesSectionOpen, setDesparasitacionesSectionOpen] = useState(false);
  const [vacunasExpanded, setVacunasExpanded] = useState(false);
  const [editingVacuna, setEditingVacuna] = useState(null);
  const [savingVacuna, setSavingVacuna] = useState(false);
  const [deletingVacunasImportadas, setDeletingVacunasImportadas] = useState(false);

  // Handler para editar una vacuna
  async function handleSaveVacuna(vacunaId, updates) {
    setSavingVacuna(true);
    const resp = await actualizarVacuna(objetoId, vacunaId, updates);
    setSavingVacuna(false);

    if (!resp.error) {
      setVacunas(prev => prev.map(v => v.id === vacunaId ? { ...v, ...updates } : v));
      setEditingVacuna(null);
      toast.success('Vacuna actualizada');
    } else {
      toast.error(resp.error);
    }
  }

  // Handler para eliminar una vacuna individual
  async function handleDeleteVacuna(vacunaId) {
    if (!vacunaId) {
      console.error('[handleDeleteVacuna] No vacunaId provided');
      toast.error('Error: ID de vacuna no válido');
      return;
    }

    console.log('[handleDeleteVacuna] Starting delete:', { objetoId, vacunaId });

    const vacuna = vacunas.find(v => v.id === vacunaId);
    const eventoMedicoId = vacuna?.eventoMedicoId;

    let visitaQuedaraVacia = false;
    let entrada = null;
    if (eventoMedicoId) {
      entrada = entradas.find(e => e.id === eventoMedicoId);
      const otrasVacunasEnVisita = vacunas.filter(v => v.eventoMedicoId === eventoMedicoId && v.id !== vacunaId);
      const despAsociadas = desparasitaciones.filter(d => d.eventoMedicoId === eventoMedicoId);
      const tieneOtrosServicios = entrada?.tipos?.some(t => t !== 'vacunacion' && t !== 'desparasitacion');
      visitaQuedaraVacia = otrasVacunasEnVisita.length === 0 && despAsociadas.length === 0 && !tieneOtrosServicios;
    }

    let mensaje = '¿Eliminar esta vacuna del registro?';
    if (visitaQuedaraVacia && entrada) {
      const fecha = new Date(entrada.fecha).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
      mensaje = `Esta es la última vacuna de la visita del ${fecha}.\n\n¿Eliminar la vacuna Y la visita?`;
    }

    if (!window.confirm(mensaje)) {
      return;
    }

    try {
      const resp = await eliminarVacuna(objetoId, vacunaId);
      console.log('[handleDeleteVacuna] Response:', resp);

      if (resp.error) {
        console.error('[handleDeleteVacuna] Error:', resp.error);
        toast.error(resp.error);
        return;
      }

      setVacunas(prev => prev.filter(v => v.id !== vacunaId));

      if (visitaQuedaraVacia && eventoMedicoId) {
        console.log('[handleDeleteVacuna] Eliminando visita vacía:', eventoMedicoId);
        // We need eliminarEntradaExpediente - import it
        const { eliminarEntradaExpediente } = await import("../../services/api");
        const respVisita = await eliminarEntradaExpediente(objetoId, eventoMedicoId);
        if (!respVisita.error) {
          setEntradas(prev => prev.filter(e => e.id !== eventoMedicoId));
          toast.success('Vacuna y visita eliminadas');
        } else {
          console.error('[handleDeleteVacuna] Error eliminando visita:', respVisita.error);
          toast.success('Vacuna eliminada (la visita no pudo ser eliminada)');
        }
      } else {
        toast.success('Vacuna eliminada');
      }
    } catch (error) {
      console.error('[handleDeleteVacuna] Exception:', error);
      toast.error('Error al eliminar la vacuna');
    }
  }

  async function handleDeleteVacunasImportadas() {
    const vacunasImportadas = vacunas.filter(v => !v.eventoMedicoId && v.importadaDeCartilla);

    if (vacunasImportadas.length === 0) {
      toast.error('No hay vacunas importadas para eliminar');
      return;
    }

    if (!window.confirm(`¿Eliminar todas las vacunas importadas (${vacunasImportadas.length})?\n\nLas vacunas de visitas médicas no se eliminarán.`)) {
      return;
    }

    setDeletingVacunasImportadas(true);
    const resp = await eliminarVacunasImportadas(objetoId);
    setDeletingVacunasImportadas(false);

    if (!resp.error) {
      setVacunas(prev => prev.filter(v => v.eventoMedicoId));
      toast.success(resp.message || 'Vacunas importadas eliminadas');
    } else {
      toast.error(resp.error);
    }
  }

  async function handleMigrateLegacyVacuna(vacuna) {
    if (!window.confirm(`¿Crear una cita médica para la vacuna "${vacuna.nombre}"?\n\nEsto vinculará la vacuna a una nueva entrada en el expediente.`)) {
      return;
    }

    try {
      const entryData = {
        fecha: `${vacuna.fecha}T12:00`,
        tipo: 'Vacunación',
        notas: `Vacuna: ${vacuna.nombre}${vacuna.laboratorio ? `\nLaboratorio: ${vacuna.laboratorio}` : ''}${vacuna.lote ? `\nLote: ${vacuna.lote}` : ''}\n\n(Migrado de registro legacy)`,
        importadaDeCartilla: true
      };

      const resp = await crearEntradaExpediente(objetoId, entryData);

      if (resp.error) {
        toast.error('Error al crear la cita médica');
        return;
      }

      const visitaId = resp.data?.id;

      const updateResp = await actualizarVacuna(objetoId, vacuna.id, {
        eventoMedicoId: visitaId
      });

      if (updateResp.error) {
        toast.error('Error al vincular la vacuna');
        return;
      }

      setVacunas(prev => prev.map(v =>
        v.id === vacuna.id ? { ...v, eventoMedicoId: visitaId } : v
      ));

      const expedienteResp = await getExpedienteDeMascota(objetoId);
      if (!expedienteResp.error) {
        setEntradas(expedienteResp.data);
      }

      toast.success('Vacuna vinculada a nueva cita médica');
    } catch (error) {
      console.error('[handleMigrateLegacyVacuna] Error:', error);
      toast.error('Error al migrar la vacuna');
    }
  }

  return (
    <>
      <div
        className="expediente-card"
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}
      >
        {/* Header del card - colapsable */}
        <div
          onClick={() => setCartillaSectionOpen(!cartillaSectionOpen)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 16px',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>💉</span>
            <span style={{ fontWeight: '600', fontSize: '15px', color: '#1f2937' }}>
              Cartilla de Salud
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {(cartillaData.vacunas.length > 0 || cartillaData.desparasitaciones.length > 0) && (
              <span style={{
                backgroundColor: '#dcfce7',
                color: '#166534',
                fontSize: '12px',
                padding: '4px 10px',
                borderRadius: '20px',
                fontWeight: '500'
              }}>
                {cartillaData.vacunas.length} vac{cartillaData.vacunas.length !== 1 ? 's' : ''}, {cartillaData.desparasitaciones.length} desp.{cartillaData.proximas.length > 0 ? `, ${cartillaData.proximas.length} prox.` : ''}
              </span>
            )}
            <i
              className={`bi bi-chevron-${cartillaSectionOpen ? 'down' : 'right'}`}
              style={{ fontSize: '14px', color: '#9ca3af' }}
            ></i>
          </div>
        </div>

        {/* Contenido colapsable de Cartilla */}
        {cartillaSectionOpen && (
        <div style={{ padding: '0 16px 16px 16px' }}>
          {/* Tratamiento activo (si hay) */}
          {tratamientosActivos.length > 0 && (
            <div
              style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-2)',
                marginBottom: 'var(--space-3)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <i className="bi bi-exclamation-circle-fill" style={{ color: '#d97706' }}></i>
                <strong style={{ color: '#92400e', fontSize: 'var(--font-size-sm)' }}>
                  EN TRATAMIENTO ACTIVO
                </strong>
              </div>
              {tratamientosActivos.slice(0, 1).map(t => (
                <div key={t.id} style={{ fontSize: 'var(--font-size-xs)', color: '#78350f' }}>
                  <div>{t.medicamentos?.[0]?.nombre || 'Tratamiento'}</div>
                  {t.fechaFin && (
                    <div>Hasta: {new Date(t.fechaFin).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                  )}
                  <button
                    className="btn btn-sm mt-1"
                    onClick={() => onVerDashboardTratamiento(t.id)}
                    style={{
                      backgroundColor: '#fbbf24',
                      color: '#78350f',
                      border: 'none',
                      fontSize: '11px',
                      padding: '2px 8px'
                    }}
                  >
                    Ver tratamiento
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Vacunas - Sección Acordeón */}
          <div style={{
            marginBottom: 'var(--space-3)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div
              onClick={() => setVacunasSectionOpen(!vacunasSectionOpen)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 12px',
                backgroundColor: '#f9fafb',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i
                  className={`bi bi-chevron-${vacunasSectionOpen ? 'down' : 'right'}`}
                  style={{ fontSize: '12px', color: 'var(--gray-500)', transition: 'transform 0.2s' }}
                ></i>
                <span style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)' }}>
                  Vacunas{cartillaData.vacunas.length > 0 ? ` (${cartillaData.vacunas.length})` : ''}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                <button
                  className="btn btn-sm"
                  onClick={() => onAddQuickEntry('Vacunación')}
                  title="Agregar vacuna"
                  style={{
                    fontSize: '11px',
                    padding: '4px 10px',
                    backgroundColor: '#fff',
                    color: '#2563eb',
                    border: '1.5px solid #2563eb',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#eff6ff'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                >
                  <i className="bi bi-plus me-1"></i>
                  Agregar
                </button>
                {!isVetAccess && vacunas.some(v => !v.eventoMedicoId && v.importadaDeCartilla) && (
                  <button
                    className="btn btn-sm"
                    onClick={handleDeleteVacunasImportadas}
                    disabled={deletingVacunasImportadas}
                    title="Eliminar vacunas importadas"
                    style={{
                      fontSize: '11px',
                      padding: '4px 10px',
                      backgroundColor: '#fff',
                      color: '#dc2626',
                      border: '1.5px solid #dc2626',
                      borderRadius: '6px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                  >
                    {deletingVacunasImportadas ? (
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                    ) : (
                      <>
                        <i className="bi bi-trash3 me-1"></i>
                        Limpiar
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            {vacunasSectionOpen && (
            <div style={{ padding: '10px 12px' }}>
            {loadingVacunas ? (
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Cargando vacunas...
              </div>
            ) : cartillaData.vacunas.length > 0 ? (
              <div style={{ fontSize: 'var(--font-size-xs)' }}>
                {(cartillaData.vacunas.length < 10 || vacunasExpanded
                  ? cartillaData.vacunas
                  : cartillaData.vacunas.slice(0, 5)
                ).map((v, idx) => (
                  <div
                    key={v.id || idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '6px',
                      marginBottom: '4px',
                      padding: '4px 6px',
                      borderRadius: '4px',
                      backgroundColor: idx % 2 === 0 ? 'transparent' : '#f9fafb'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, color: 'var(--gray-600)' }}>
                      <i className="bi bi-check-circle-fill" style={{ color: '#10b981', fontSize: '12px' }}></i>
                      <span style={{ fontWeight: '500' }}>{v.nombre || 'Vacuna'}</span>
                      <span style={{ color: 'var(--gray-400)' }}>
                        ({new Date(v.fecha).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })})
                      </span>
                      {v.importadaDeCartilla && (
                        <span style={{
                          backgroundColor: v.origenImportacion === 'manual' ? '#dcfce7' : '#f3e8ff',
                          color: v.origenImportacion === 'manual' ? '#166534' : '#7c3aed',
                          fontSize: '9px',
                          padding: '1px 4px',
                          borderRadius: '4px'
                        }}>
                          {v.origenImportacion === 'manual' ? 'Manual' : 'IA'}
                        </span>
                      )}
                    </div>
                    {v.eventoMedicoId ? (
                      <button
                        className="btn btn-sm"
                        onClick={() => onOpenVisitaModal(v.eventoMedicoId)}
                        title="Ver cita médica asociada"
                        style={{
                          padding: '2px 6px',
                          fontSize: '10px',
                          backgroundColor: '#eff6ff',
                          color: '#2563eb',
                          border: '1px solid #bfdbfe'
                        }}
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span
                          style={{
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            fontSize: '9px',
                            padding: '1px 4px',
                            borderRadius: '4px'
                          }}
                          title="Esta vacuna no tiene una cita médica asociada"
                        >
                          Sin cita
                        </span>
                        {!isVetAccess && (
                          <button
                            className="btn btn-sm"
                            onClick={() => handleMigrateLegacyVacuna(v)}
                            title="Crear cita médica para esta vacuna"
                            style={{
                              padding: '2px 6px',
                              fontSize: '10px',
                              backgroundColor: '#f0fdf4',
                              color: '#16a34a',
                              border: '1px solid #bbf7d0'
                            }}
                          >
                            <i className="bi bi-link-45deg"></i>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {cartillaData.vacunas.length >= 10 && (
                  <button
                    className="btn btn-sm w-100 mt-2"
                    onClick={() => setVacunasExpanded(!vacunasExpanded)}
                    style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: 'none'
                    }}
                  >
                    {vacunasExpanded ? (
                      <>
                        <i className="bi bi-chevron-up me-1"></i>
                        Ver menos
                      </>
                    ) : (
                      <>
                        <i className="bi bi-chevron-down me-1"></i>
                        Ver todas ({cartillaData.vacunas.length - 5} más)
                      </>
                    )}
                  </button>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                Sin vacunas registradas
              </div>
            )}
            </div>
            )}
          </div>

          {/* Desparasitaciones - Sección Acordeón */}
          <div style={{
            marginBottom: 'var(--space-3)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div
              onClick={() => setDesparasitacionesSectionOpen(!desparasitacionesSectionOpen)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 12px',
                backgroundColor: '#f9fafb',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i
                  className={`bi bi-chevron-${desparasitacionesSectionOpen ? 'down' : 'right'}`}
                  style={{ fontSize: '12px', color: 'var(--gray-500)', transition: 'transform 0.2s' }}
                ></i>
                <span style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)' }}>
                  Desparasitaciones{cartillaData.desparasitaciones.length > 0 ? ` (${cartillaData.desparasitaciones.length})` : ''}
                </span>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <button
                  className="btn btn-sm"
                  onClick={() => onAddQuickEntry('Desparasitación')}
                  title="Agregar desparasitación"
                  style={{
                    fontSize: '11px',
                    padding: '4px 10px',
                    backgroundColor: '#fff',
                    color: '#2563eb',
                    border: '1.5px solid #2563eb',
                    borderRadius: '6px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#eff6ff'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                >
                  <i className="bi bi-plus me-1"></i>
                  Agregar
                </button>
              </div>
            </div>
            {desparasitacionesSectionOpen && (
            <div style={{ padding: '10px 12px' }}>
            {loadingDesparasitaciones ? (
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Cargando desparasitaciones...
              </div>
            ) : cartillaData.desparasitaciones.length > 0 ? (
              <div style={{ fontSize: 'var(--font-size-xs)' }}>
                {cartillaData.desparasitaciones.map((d, idx) => (
                  <div key={d.id || idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', color: 'var(--gray-600)' }}>
                    <i className="bi bi-check-circle-fill" style={{ color: '#10b981', fontSize: '12px' }}></i>
                    <span style={{ fontWeight: '500' }}>{d.producto || 'Desparasitacion'}</span>
                    {d.fecha && (
                      <span style={{ color: 'var(--gray-400)' }}>
                        ({new Date(d.fecha).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })})
                      </span>
                    )}
                    {d.importadaDeCartilla && (
                      <span style={{
                        backgroundColor: d.origenImportacion === 'manual' ? '#dcfce7' : '#f3e8ff',
                        color: d.origenImportacion === 'manual' ? '#166534' : '#7c3aed',
                        fontSize: '9px',
                        padding: '1px 4px',
                        borderRadius: '4px'
                      }}>
                        {d.origenImportacion === 'ia' ? 'IA' : 'Manual'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)' }}>
                Sin desparasitaciones registradas
              </div>
            )}
            </div>
            )}
          </div>

          {/* Próximas */}
          <ProximasSection
            cartillaData={cartillaData}
            objetoId={objetoId}
            mascotaInfo={mascotaInfo}
            isVetAccess={isVetAccess}
            proximasConRecordatorio={proximasConRecordatorio}
            setProximasConRecordatorio={setProximasConRecordatorio}
            creandoRecordatorio={creandoRecordatorio}
            setCreandoRecordatorio={setCreandoRecordatorio}
            setVacunas={setVacunas}
            setDesparasitaciones={setDesparasitaciones}
          />

          {/* Botones de la cartilla */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
            <button
              className="btn btn-sm"
              onClick={onCompartir}
              disabled={loadingShare}
              style={{
                fontSize: '12px',
                padding: '6px 12px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px'
              }}
            >
              <i className="bi bi-share me-1"></i>
              Compartir
            </button>
            <button
              className="btn btn-sm"
              onClick={onExportCartilla}
              disabled={loadingCartillaExport || (cartillaData.vacunas.length === 0 && cartillaData.desparasitaciones.length === 0)}
              style={{
                fontSize: '12px',
                padding: '6px 12px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px'
              }}
            >
              {loadingCartillaExport ? (
                <span className="spinner-border spinner-border-sm" role="status"></span>
              ) : (
                <>
                  <i className="bi bi-file-earmark-pdf me-1"></i>
                  Exportar a PDF
                </>
              )}
            </button>
            {!isVetAccess && (
              <button
                className="btn btn-sm"
                onClick={onShowQuickReminder}
                style={{
                  fontSize: '12px',
                  padding: '6px 12px',
                  backgroundColor: '#fffbeb',
                  color: '#92400e',
                  border: '1px solid #fcd34d',
                  borderRadius: '6px',
                  fontWeight: '500',
                }}
              >
                <i className="bi bi-bell me-1"></i>
                Ver recordatorios
              </button>
            )}
            {canUseIA ? (
              <button
                className="btn btn-sm"
                onClick={onShowImportCartilla}
                style={{
                  fontSize: '12px',
                  padding: '6px 12px',
                  backgroundColor: '#f0fdf4',
                  color: '#16a34a',
                  border: '1px solid #bbf7d0',
                  borderRadius: '6px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#dcfce7'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f0fdf4'}
              >
                <i className="bi bi-camera me-1"></i>
                Importar desde Foto
              </button>
            ) : isStandardPlan && (
              <button
                className="btn btn-sm"
                disabled
                title="Disponible en plan Premium"
                style={{
                  fontSize: '12px',
                  padding: '6px 12px',
                  backgroundColor: '#f3f4f6',
                  color: '#9ca3af',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '500',
                  cursor: 'not-allowed'
                }}
              >
                <i className="bi bi-lock me-1"></i>
                Importar desde Foto
              </button>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Modal de Editar Vacuna */}
      {editingVacuna && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100000,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-4)'
          }}
          onClick={() => setEditingVacuna(null)}
        >
          <div
            className="card card-elevated"
            style={{
              borderRadius: 'var(--radius-xl)',
              maxWidth: '500px',
              width: '100%',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-header-gradient d-flex justify-content-between align-items-center"
              style={{ padding: 'var(--space-3) var(--space-4)' }}
            >
              <h5
                className="mb-0"
                style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--white)'
                }}
              >
                <i className="bi bi-pencil me-2"></i>
                Editar Vacuna
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => setEditingVacuna(null)}
              ></button>
            </div>

            <div className="card-body" style={{ padding: 'var(--space-4)' }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  handleSaveVacuna(editingVacuna.id, {
                    nombre: formData.get('nombre'),
                    fecha: formData.get('fecha'),
                    laboratorio: formData.get('laboratorio') || null,
                    lote: formData.get('lote') || null,
                    caducidad: formData.get('caducidad') || null,
                    proximaVacunacion: formData.get('proximaVacunacion') || null
                  });
                }}
              >
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>
                    Nombre de la vacuna *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="nombre"
                    defaultValue={editingVacuna.nombre}
                    required
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>
                    Fecha de aplicación *
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="fecha"
                    defaultValue={editingVacuna.fecha}
                    required
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>
                    Laboratorio
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="laboratorio"
                    defaultValue={editingVacuna.laboratorio || ''}
                    placeholder="Ej: Zoetis, MSD, Virbac..."
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  />
                </div>

                <div className="row">
                  <div className="col-6 mb-3">
                    <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>
                      Número de lote
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="lote"
                      defaultValue={editingVacuna.lote || ''}
                      style={{ fontSize: 'var(--font-size-sm)' }}
                    />
                  </div>
                  <div className="col-6 mb-3">
                    <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>
                      Fecha de caducidad
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="caducidad"
                      defaultValue={editingVacuna.caducidad || ''}
                      style={{ fontSize: 'var(--font-size-sm)' }}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>
                    Próxima vacunación
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="proximaVacunacion"
                    defaultValue={editingVacuna.proximaVacunacion || ''}
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  />
                </div>

                <div className="d-flex gap-2 justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setEditingVacuna(null)}
                    disabled={savingVacuna}
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={savingVacuna}
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {savingVacuna ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check2 me-2"></i>
                        Guardar cambios
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
